import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Loader } from '@getro/rombo';

import BackupCodeToggle from './BackupCodeToggle';

const onlyDigits = (value = '') => value.replace(/\D/g, '');

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const BoxesWrapper = styled.div`
  width: 100%;
  position: relative;
  max-width: 440px;
`;

const Boxes = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const LoadingOverlay = styled.div.attrs({ 'data-cy': 'mfa-digit-loading-overlay' })`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
`;

const Digit = styled.input`
  width: 64px;
  height: 64px;
  padding: 0;
  text-align: center;
  font-family: inherit;
  font-size: 22px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.main};
  background-color: ${({ theme }) => theme.colors.neutral[0]};
  border: 1px solid ${({ theme, hasError }) => (hasError ? theme.colors.icon.error : theme.colors.border.default)};
  border-radius: 4px;
  outline: none;
  transition: border-color 0.15s ease-in-out;
  -moz-appearance: textfield;

  &:focus {
    border-color: ${({ theme, hasError }) => (hasError ? theme.colors.icon.error : theme.colors.primary)};
    box-shadow: 0 0 0 1px ${({ theme, hasError }) => (hasError ? theme.colors.icon.error : theme.colors.primary)};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.border.subtle};
    cursor: not-allowed;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const MfaDigitInput = ({
  length,
  value,
  onChange,
  onComplete,
  disabled,
  loading,
  hasError,
  autoFocus,
  name,
  ariaLabel,
  onUseBackupCode,
  backupCodeToggleDataCy,
  focusToken,
}) => {
  const inputsRef = useRef([]);

  const digits = useMemo(() => {
    const chars = onlyDigits(value).slice(0, length).split('');
    return Array.from({ length }, (_, index) => chars[index] ?? '');
  }, [value, length]);

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (focusToken) {
      inputsRef.current[0]?.focus();
    }
  }, [focusToken]);

  const focusInput = (index) => {
    const clamped = Math.max(0, Math.min(index, length - 1));
    inputsRef.current[clamped]?.focus();
    inputsRef.current[clamped]?.select();
  };

  const commit = (nextDigits, caretIndex) => {
    const next = nextDigits.join('');
    onChange(next);

    if (typeof caretIndex === 'number') {
      focusInput(caretIndex);
    }

    if (next.length === length) {
      onComplete?.(next);
    }
  };

  const fillFrom = (startIndex, incoming) => {
    const next = [...digits];
    let cursor = startIndex;

    onlyDigits(incoming)
      .split('')
      .forEach((digit) => {
        if (cursor < length) {
          next[cursor] = digit;
          cursor += 1;
        }
      });

    commit(next, cursor);
  };

  const handleChange = (index, event) => {
    const typed = onlyDigits(event.target.value);

    if (!typed) {
      const next = [...digits];
      next[index] = '';
      commit(next);
      return;
    }

    if (typed.length > 1) {
      fillFrom(index, typed);
      return;
    }

    const next = [...digits];
    next[index] = typed;
    commit(next, index + 1);
  };

  const handleKeyDown = (index, event) => {
    switch (event.key) {
      case 'Backspace': {
        event.preventDefault();
        const next = [...digits];
        if (digits[index]) {
          next[index] = '';
          commit(next, index);
        } else if (index > 0) {
          next[index - 1] = '';
          commit(next, index - 1);
        }
        break;
      }
      case 'ArrowLeft':
        event.preventDefault();
        focusInput(index - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        focusInput(index + 1);
        break;
      default:
        break;
    }
  };

  const handlePaste = (index, event) => {
    event.preventDefault();
    fillFrom(index, event.clipboardData.getData('text'));
  };

  return (
    <Root>
      <BoxesWrapper aria-busy={loading}>
        <Boxes>
          {digits.map((digit, index) => (
            <Digit
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              ref={(element) => {
                inputsRef.current[index] = element;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              name={name ? `${name}-${index}` : undefined}
              aria-label={`${ariaLabel} digit ${index + 1}`}
              value={digit}
              disabled={disabled}
              hasError={hasError}
              onChange={(event) => handleChange(index, event)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onPaste={(event) => handlePaste(index, event)}
              onFocus={(event) => event.target.select()}
            />
          ))}
        </Boxes>
        {loading && (
          <LoadingOverlay>
            <Loader size="medium" />
          </LoadingOverlay>
        )}
      </BoxesWrapper>
      {onUseBackupCode && <BackupCodeToggle onToggle={onUseBackupCode} data-cy={backupCodeToggleDataCy} />}
    </Root>
  );
};

MfaDigitInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  hasError: PropTypes.bool,
  autoFocus: PropTypes.bool,
  name: PropTypes.string,
  ariaLabel: PropTypes.string,
  onUseBackupCode: PropTypes.func,
  backupCodeToggleDataCy: PropTypes.string,
  focusToken: PropTypes.number,
};

MfaDigitInput.defaultProps = {
  length: 6,
  value: '',
  onComplete: null,
  disabled: false,
  loading: false,
  hasError: false,
  autoFocus: true,
  name: undefined,
  ariaLabel: 'Verification code',
  onUseBackupCode: undefined,
  backupCodeToggleDataCy: undefined,
  focusToken: 0,
};

export default MfaDigitInput;
